import { IPublicClientApplication } from '@azure/msal-browser';
import { dvList, dvCreate, dvUpdate, dvDelete } from '../api/dataverseClient';
import { getDataverseHeaders } from '../auth/authService';
import { getDataverseApiBase } from './environmentService';

// ── Work Calendars ─────────────────────────────────────────────────────
//
// Three tables (schema created April 2026, first UI built here):
//   dagrun_pm_calendar          — a named work calendar (work days + hours/day + default)
//   dagrun_pm_calendarexception — holidays / non-working / extra-working dates for a calendar
//   dagrun_pm_resourcecalendar  — assigns a calendar to a worker on a specific project
//
// WORK-DAYS BITMASK convention (dagrun_pm_workdaysbitmask, Integer): bit d is set
// when weekday d is a working day, where d = JS Date.getUTCDay() (0=Sun … 6=Sat).
// Mon–Fri therefore = bits 1..5 = 62. This is the single source of truth for the
// convention across the app.

export const ExceptionType = { Holiday: 100000000, NonWorking: 100000001, ExtraWorking: 100000002 } as const;
export const MON_FRI_BITMASK = (1 << 1) | (1 << 2) | (1 << 3) | (1 << 4) | (1 << 5); // 62
export const FALLBACK_HOURS_PER_DAY = 8;

export const WEEKDAYS: { day: number; label: string; short: string }[] = [
  { day: 1, label: 'Monday', short: 'Mon' },
  { day: 2, label: 'Tuesday', short: 'Tue' },
  { day: 3, label: 'Wednesday', short: 'Wed' },
  { day: 4, label: 'Thursday', short: 'Thu' },
  { day: 5, label: 'Friday', short: 'Fri' },
  { day: 6, label: 'Saturday', short: 'Sat' },
  { day: 0, label: 'Sunday', short: 'Sun' },
];

export interface WorkCalendar {
  dagrun_pm_calendarid: string;
  dagrun_pm_name: string | null;
  dagrun_pm_workdaysbitmask: number | null;
  dagrun_pm_hoursperday: number | null;
  dagrun_pm_isdefault: boolean | null;
  dagrun_pm_notes: string | null;
}
export interface CalendarException {
  dagrun_pm_calendarexceptionid: string;
  _dagrun_pm_calendarid_value: string | null;
  dagrun_pm_exceptiondate: string | null;
  dagrun_pm_exceptiontype: number | null;
  dagrun_pm_description: string | null;
}
export interface ResourceCalendarAssignment {
  dagrun_pm_resourcecalendarid: string;
  _dagrun_pm_calendarid_value: string | null;
  '_dagrun_pm_calendarid_value@OData.Community.Display.V1.FormattedValue'?: string;
  _dagrun_pm_projectid_value: string | null;
  dagrun_pm_userid: string | null;
  dagrun_pm_effectivefrom: string | null;
  dagrun_pm_effectiveto: string | null;
}

/** A fully-resolved capacity model for one worker (the fallback-chain output). */
export interface ResolvedCalendar {
  source: 'project' | 'tenant-default' | 'fallback';
  calendarId: string | null;
  name: string;
  workDaysBitmask: number;
  hoursPerDay: number;
  exceptionsByDay: Map<string, number>; // 'yyyy-mm-dd' → ExceptionType
}

const CAL_FIELDS = ['dagrun_pm_calendarid', 'dagrun_pm_name', 'dagrun_pm_workdaysbitmask', 'dagrun_pm_hoursperday', 'dagrun_pm_isdefault', 'dagrun_pm_notes'];
const EXC_FIELDS = ['dagrun_pm_calendarexceptionid', '_dagrun_pm_calendarid_value', 'dagrun_pm_exceptiondate', 'dagrun_pm_exceptiontype', 'dagrun_pm_description'];
const RC_FIELDS = ['dagrun_pm_resourcecalendarid', '_dagrun_pm_calendarid_value', '_dagrun_pm_projectid_value', 'dagrun_pm_userid', 'dagrun_pm_effectivefrom', 'dagrun_pm_effectiveto'];

const dayKey = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

// ── Calendars CRUD ─────────────────────────────────────────────────────

export async function listCalendars(pca: IPublicClientApplication): Promise<WorkCalendar[]> {
  return dvList<WorkCalendar>(pca, 'dagrun_pm_calendars', { select: CAL_FIELDS, orderby: 'dagrun_pm_name asc', top: 200 });
}

export async function createCalendar(pca: IPublicClientApplication, input: { name: string; workDaysBitmask: number; hoursPerDay: number; notes?: string | null; isDefault?: boolean }): Promise<string> {
  const id = await dvCreate(pca, 'dagrun_pm_calendars', {
    dagrun_pm_name: input.name,
    dagrun_pm_workdaysbitmask: input.workDaysBitmask,
    dagrun_pm_hoursperday: input.hoursPerDay,
    dagrun_pm_isdefault: false,
    ...(input.notes ? { dagrun_pm_notes: input.notes } : {}),
  });
  if (input.isDefault) await setDefaultCalendar(pca, id);
  return id;
}

export async function updateCalendar(pca: IPublicClientApplication, id: string, patch: { name?: string; workDaysBitmask?: number; hoursPerDay?: number; notes?: string | null }): Promise<void> {
  const body: Record<string, unknown> = {};
  if (patch.name !== undefined) body.dagrun_pm_name = patch.name;
  if (patch.workDaysBitmask !== undefined) body.dagrun_pm_workdaysbitmask = patch.workDaysBitmask;
  if (patch.hoursPerDay !== undefined) body.dagrun_pm_hoursperday = patch.hoursPerDay;
  if (patch.notes !== undefined) body.dagrun_pm_notes = patch.notes;
  await dvUpdate(pca, 'dagrun_pm_calendars', id, body);
}

export async function deleteCalendar(pca: IPublicClientApplication, id: string): Promise<void> {
  // Manual cascade of exceptions (avoid orphans); resource assignments keep a
  // nulled lookup and fall back to tenant-default per the chain.
  try {
    const excs = await listExceptions(pca, id);
    for (const e of excs) { try { await dvDelete(pca, 'dagrun_pm_calendarexceptions', e.dagrun_pm_calendarexceptionid); } catch { /* best effort */ } }
  } catch { /* ignore */ }
  await dvDelete(pca, 'dagrun_pm_calendars', id);
}

/**
 * Make `id` the tenant default AND unset every other default in ONE atomic
 * $batch changeset — never two user-visible steps, never a transient
 * two-defaults or zero-defaults state.
 */
export async function setDefaultCalendar(pca: IPublicClientApplication, id: string): Promise<void> {
  const all = await listCalendars(pca);
  const toUnset = all.filter(c => c.dagrun_pm_isdefault && c.dagrun_pm_calendarid !== id);
  const base = getDataverseApiBase();
  const headers = await getDataverseHeaders(pca);
  const boundary = 'batch_dagrun_cal';
  const cs = 'changeset_dagrun_cal';
  const ops = [
    ...toUnset.map(c => ({ id: c.dagrun_pm_calendarid, val: false })),
    { id, val: true },
  ];
  let body = `--${boundary}\r\nContent-Type: multipart/mixed;boundary=${cs}\r\n\r\n`;
  ops.forEach((op, i) => {
    body += `--${cs}\r\nContent-Type: application/http\r\nContent-Transfer-Encoding: binary\r\nContent-ID: ${i + 1}\r\n\r\n`;
    body += `PATCH ${base}/dagrun_pm_calendars(${op.id}) HTTP/1.1\r\nContent-Type: application/json\r\n\r\n`;
    body += `${JSON.stringify({ dagrun_pm_isdefault: op.val })}\r\n`;
  });
  body += `--${cs}--\r\n--${boundary}--\r\n`;
  const res = await fetch(`${base}/$batch`, {
    method: 'POST',
    headers: { ...headers, 'Content-Type': `multipart/mixed;boundary=${boundary}` },
    body,
  });
  if (!res.ok) throw new Error(`Set default calendar failed (${res.status}): ${(await res.text()).slice(0, 300)}`);
}

// ── Exceptions CRUD ────────────────────────────────────────────────────

export async function listExceptions(pca: IPublicClientApplication, calendarId: string): Promise<CalendarException[]> {
  return dvList<CalendarException>(pca, 'dagrun_pm_calendarexceptions', {
    select: EXC_FIELDS, filter: `_dagrun_pm_calendarid_value eq ${calendarId}`, orderby: 'dagrun_pm_exceptiondate asc', top: 500,
  });
}

export async function createException(pca: IPublicClientApplication, input: { calendarId: string; date: string; type: number; description?: string | null }): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_calendarexceptions', {
    'dagrun_pm_calendarid@odata.bind': `/dagrun_pm_calendars(${input.calendarId})`,
    dagrun_pm_exceptiondate: input.date,
    dagrun_pm_exceptiontype: input.type,
    ...(input.description ? { dagrun_pm_description: input.description } : {}),
  });
}

export async function deleteException(pca: IPublicClientApplication, id: string): Promise<void> {
  await dvDelete(pca, 'dagrun_pm_calendarexceptions', id);
}

// ── Resource calendar assignments CRUD ─────────────────────────────────

export async function listResourceCalendars(pca: IPublicClientApplication, projectId: string): Promise<ResourceCalendarAssignment[]> {
  return dvList<ResourceCalendarAssignment>(pca, 'dagrun_pm_resourcecalendars', {
    select: RC_FIELDS, filter: `_dagrun_pm_projectid_value eq ${projectId}`, top: 500,
  });
}

export async function createResourceCalendar(pca: IPublicClientApplication, input: { projectId: string; userId: string; calendarId: string; from?: string | null; to?: string | null }): Promise<string> {
  return dvCreate(pca, 'dagrun_pm_resourcecalendars', {
    'dagrun_pm_projectid@odata.bind': `/dagrun_pm_projects(${input.projectId})`,
    'dagrun_pm_calendarid@odata.bind': `/dagrun_pm_calendars(${input.calendarId})`,
    dagrun_pm_userid: input.userId,
    ...(input.from ? { dagrun_pm_effectivefrom: input.from } : {}),
    ...(input.to ? { dagrun_pm_effectiveto: input.to } : {}),
  });
}

export async function deleteResourceCalendar(pca: IPublicClientApplication, id: string): Promise<void> {
  await dvDelete(pca, 'dagrun_pm_resourcecalendars', id);
}

// ── Fallback chain ─────────────────────────────────────────────────────

export async function getTenantDefaultCalendar(pca: IPublicClientApplication): Promise<WorkCalendar | null> {
  const rows = await dvList<WorkCalendar>(pca, 'dagrun_pm_calendars', {
    select: CAL_FIELDS, filter: 'dagrun_pm_isdefault eq true', orderby: 'createdon asc', top: 1,
  });
  return rows[0] ?? null;
}

function toResolved(cal: WorkCalendar, source: ResolvedCalendar['source'], excs: CalendarException[]): ResolvedCalendar {
  const exceptionsByDay = new Map<string, number>();
  for (const e of excs) {
    if (e.dagrun_pm_exceptiondate && e.dagrun_pm_exceptiontype != null) {
      exceptionsByDay.set(e.dagrun_pm_exceptiondate.slice(0, 10), e.dagrun_pm_exceptiontype);
    }
  }
  return {
    source, calendarId: cal.dagrun_pm_calendarid, name: cal.dagrun_pm_name ?? 'Calendar',
    workDaysBitmask: cal.dagrun_pm_workdaysbitmask ?? MON_FRI_BITMASK,
    hoursPerDay: cal.dagrun_pm_hoursperday ?? FALLBACK_HOURS_PER_DAY,
    exceptionsByDay,
  };
}

export const HARDCODED_FALLBACK: ResolvedCalendar = {
  source: 'fallback', calendarId: null, name: 'Default (Mon–Fri, 8h)',
  workDaysBitmask: MON_FRI_BITMASK, hoursPerDay: FALLBACK_HOURS_PER_DAY, exceptionsByDay: new Map(),
};

/**
 * THE fallback chain (single source of truth — do not inline elsewhere):
 *   1. A dagrun_pm_resourcecalendar for THIS project + worker (in effect on `onDate`) → its calendar
 *   2. else the tenant default dagrun_pm_calendar
 *   3. else the hardcoded fallback (Mon–Fri, 8h/day)
 */
export async function resolveResourceCalendar(
  pca: IPublicClientApplication, projectId: string, userId: string, onDate: Date = new Date(),
): Promise<ResolvedCalendar> {
  const onMs = onDate.getTime();
  // 1) project-specific
  try {
    const assigns = await dvList<ResourceCalendarAssignment>(pca, 'dagrun_pm_resourcecalendars', {
      select: RC_FIELDS, filter: `_dagrun_pm_projectid_value eq ${projectId} and dagrun_pm_userid eq '${userId}'`, top: 50,
    });
    const active = assigns.find(a => a._dagrun_pm_calendarid_value && inEffect(a, onMs));
    if (active?._dagrun_pm_calendarid_value) {
      const cal = await getCalendarById(pca, active._dagrun_pm_calendarid_value);
      if (cal) return toResolved(cal, 'project', await listExceptions(pca, cal.dagrun_pm_calendarid));
    }
  } catch { /* fall through */ }
  // 2) tenant default
  try {
    const def = await getTenantDefaultCalendar(pca);
    if (def) return toResolved(def, 'tenant-default', await listExceptions(pca, def.dagrun_pm_calendarid));
  } catch { /* fall through */ }
  // 3) hardcoded
  return { ...HARDCODED_FALLBACK, exceptionsByDay: new Map() };
}

/**
 * Bulk fallback resolution for many workers on one project — one pass of
 * queries (assignments + default + exceptions) instead of N. Same semantics as
 * resolveResourceCalendar; used by the project Resources tab (Block B).
 */
export async function resolveProjectCalendars(
  pca: IPublicClientApplication, projectId: string, userIds: string[], onDate: Date = new Date(),
): Promise<Map<string, ResolvedCalendar>> {
  const onMs = onDate.getTime();
  const [assigns, def, allCals] = await Promise.all([
    dvList<ResourceCalendarAssignment>(pca, 'dagrun_pm_resourcecalendars', { select: RC_FIELDS, filter: `_dagrun_pm_projectid_value eq ${projectId}`, top: 500 }).catch(() => [] as ResourceCalendarAssignment[]),
    getTenantDefaultCalendar(pca).catch(() => null),
    listCalendars(pca).catch(() => [] as WorkCalendar[]),
  ]);
  const calById = new Map(allCals.map(c => [c.dagrun_pm_calendarid, c]));
  // Exceptions only for calendars we might use (default + any assigned).
  const neededCalIds = new Set<string>();
  if (def) neededCalIds.add(def.dagrun_pm_calendarid);
  for (const a of assigns) if (a._dagrun_pm_calendarid_value) neededCalIds.add(a._dagrun_pm_calendarid_value);
  const excsByCal = new Map<string, CalendarException[]>();
  await Promise.all([...neededCalIds].map(async cid => { excsByCal.set(cid, await listExceptions(pca, cid).catch(() => [])); }));

  const out = new Map<string, ResolvedCalendar>();
  for (const uid of userIds) {
    const active = assigns.find(a => a.dagrun_pm_userid === uid && a._dagrun_pm_calendarid_value && inEffect(a, onMs));
    if (active?._dagrun_pm_calendarid_value) {
      const cal = calById.get(active._dagrun_pm_calendarid_value);
      if (cal) { out.set(uid, toResolved(cal, 'project', excsByCal.get(cal.dagrun_pm_calendarid) ?? [])); continue; }
    }
    if (def) { out.set(uid, toResolved(def, 'tenant-default', excsByCal.get(def.dagrun_pm_calendarid) ?? [])); continue; }
    out.set(uid, { ...HARDCODED_FALLBACK, exceptionsByDay: new Map() });
  }
  return out;
}

async function getCalendarById(pca: IPublicClientApplication, id: string): Promise<WorkCalendar | null> {
  const rows = await dvList<WorkCalendar>(pca, 'dagrun_pm_calendars', { select: CAL_FIELDS, filter: `dagrun_pm_calendarid eq ${id}`, top: 1 });
  return rows[0] ?? null;
}

function inEffect(a: ResourceCalendarAssignment, onMs: number): boolean {
  const from = a.dagrun_pm_effectivefrom ? new Date(a.dagrun_pm_effectivefrom).getTime() : -Infinity;
  const to = a.dagrun_pm_effectiveto ? new Date(a.dagrun_pm_effectiveto).getTime() : Infinity;
  return onMs >= from && onMs <= to;
}

// ── Capacity helper (consumed by the workload math in Block B) ─────────

/** Whether a given day is a working day under a resolved calendar: the
 *  work-days bitmask, with per-date exceptions overriding it (Holiday /
 *  NonWorking → off; ExtraWorking → on). */
export function isWorkingDayFor(cal: ResolvedCalendar, dayMs: number): boolean {
  const exc = cal.exceptionsByDay.get(dayKey(dayMs));
  if (exc === ExceptionType.Holiday || exc === ExceptionType.NonWorking) return false;
  if (exc === ExceptionType.ExtraWorking) return true;
  const d = new Date(dayMs).getUTCDay();
  return ((cal.workDaysBitmask >> d) & 1) === 1;
}
